import React, { useEffect, useState, useCallback, useRef } from 'react';
import Header from '../../components/common/global-top-bar';
import { useDispatch, connect } from 'react-redux';
import { CustomInput, ToggleButton, Spinner, DropDown, DeBounce } from '../../components/common';
import { useHistory } from 'react-router';
import { Location } from 'history';
import { ROUTE_PATH } from '../../routes/paths';
import {
  fetchCommsListRequest,
  fetchSystemMessageGroupsRequest,
  postCommsFailure,
  postCommsRequest,
  postRepairTemplateRequest,
  postRepairTemplateSuccess,
  postTestCommsRequest,
  removeCommsFailure,
  removeCommsRequest,
  updateCommsFailure,
  updateCommsRequest,
} from './redux-templates/actions';
import Modal from '../../components/common/modal';
import TemplateModel from '../../components/templates/templateModel';
import EmailPreviewModal from '../../components/templates/emailPreviewModal';
// Searchbar is a forwardRef component whose typing conflicts with this project's JSX typings.
// Use a simple input here instead.
import { toast } from 'react-toastify';
import RouteLeavingGuard from '../../components/route-leaving-prompt';

import { useSMSChannels, SMSChannel } from '../../hooks/useSMSChannels';
import { useDomains } from '../../hooks/useClients';
import { SystemMessageType } from './redux-templates/types';
import { EmailTemplateMetadata, useEmailTemplateMetadata } from '../../hooks/useComms';
import {
  AUDIENCE_FILTERS,
  AUDIENCE_OPTIONS,
  AudienceFilterKey,
  CHANNEL_TYPE_FILTERS,
  ChannelTypeFilterKey,
  COMMS_IA,
  GroupFilterKey,
  SystemMessageAudience,
  applyChannelTypeFilter,
  buildCommsListParams,
  formatEmailTemplateOptionLabel,
  resolveCommsSaveDomainId,
  templatesForPicker,
} from '../../utils/emailTemplates';

type props = {
  comms: any;
  SystemMessageGroups: any;
  pending: boolean;
  listPending: boolean;
  UpdateComms: any;
  RemoveComms: any;
  CreateComms: any;
  RepairTemplate: any;
};

type CommDetailsProps = {
  data: any;
  SystemMessageGroups: any;
  emailTemplates: EmailTemplateMetadata[];
  RemoveComms: any;
  handleContinueSave: (path: string) => void;
};

const CommsDetails: React.FunctionComponent<CommDetailsProps> = (CommDetailsProps): JSX.Element => {
  const [messageName, setMessageName] = useState<string>('');
  const [messageGroup, setMessageGroup] = useState<string>();
  const [messageAudience, setMessageAudience] = useState<SystemMessageAudience>('shared');
  const [messagekey, setMessageKey] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [textMessage, setTextMessage] = useState<string>('');
  const [enableEmail, setEnableEmail] = useState<boolean>(false);
  const [emailTemplate, setEmailTemplate] = useState<string>('No Template');
  const [sendAsSMS, setSendAsSMS] = useState<boolean>(false);
  const [sendSMSAsPrimary, setSendSMSAsPrimary] = useState<boolean>(false);
  const [internalNotes, setInternalNotes] = useState<string>('');
  const [delayWhileOnCall, setDelayWhileOnCall] = useState<boolean>(true);
  const [testEmailAddress, setTestEmailAddress] = useState<string>(localStorage.getItem('commsTestEmail') || '');
  const [enableTestPopUP, setEnableTestPopUP] = useState<boolean>(false);
  const [showEmailPreview, setShowEmailPreview] = useState<boolean>(false);
  const [veriyCheckOldMessage, setVerifyCheckOldMessage] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [enableSaveOption, setEnableSaveOption] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [smsChannelId, setSMSChannelId] = useState<string>('');
  const dispatch = useDispatch();
  const history = useHistory();

  const { data: availableSMSChannels } = useSMSChannels();
  const { data: domains = [] } = useDomains();

  const getAvailableSMSChannelOptions = () => {
    const options = [];
    options.push(
      <option key="channel_none" value="">
        Use Default
      </option>,
    );

    if (availableSMSChannels) {
      return options.concat(
        availableSMSChannels.map((item: SMSChannel) => {
          return (
            <option key={`channel_${item.id}`} value={String(item.id)}>
              {item.name} ({item.channel_type})
            </option>
          );
        }),
      );
    }
    return options;
  };

  useEffect(() => {
    if (CommDetailsProps?.data && CommDetailsProps.SystemMessageGroups) {
      const data = CommDetailsProps?.data;
      if (data) {
        setEnableSaveOption(false);
        setMessageName(data?.name);
        setMessageGroup(data?.group_id);
        setMessageAudience((data?.audience as SystemMessageAudience) || 'shared');
        setMessageKey(data.key);
        setVerifyCheckOldMessage(false);
        setEmailTemplate(data.email_template || 'No Template');
        setTextMessage(data.message);
        setEnableEmail(data.send_email);
        setSendSMSAsPrimary(data.send_sms_as_primary);
        setInternalNotes(data.description);
        setSubject(data.subject);
        setSendAsSMS(data.send_sms);
        setSMSChannelId(data.sms_channel_id != null ? String(data.sms_channel_id) : '');
        setDelayWhileOnCall(data.delay_while_on_call);
      }
    } else {
      setDefaultValues();
    }
  }, [CommDetailsProps?.data, CommDetailsProps.SystemMessageGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDefaultValues = useCallback(() => {
    setMessageName('');
    setMessageGroup('');
    setMessageAudience('shared');
    setMessageKey('');
    setEmailTemplate('');
    setTextMessage('');
    setVerifyCheckOldMessage(false);
    setEnableEmail(false);
    setSendSMSAsPrimary(false);
    setInternalNotes('');
    setSubject('');
    setSendAsSMS(false);
    CommDetailsProps.handleContinueSave('');
    setSMSChannelId('');
    setDelayWhileOnCall(true);
  }, [CommDetailsProps]);

  useEffect(() => {
    setEnableSaveOption(false);
  }, []);

  useEffect(() => {
    if (emailTemplate && CommDetailsProps.emailTemplates?.length > 0) {
      const check = CommDetailsProps.emailTemplates.some((item) => item.id === emailTemplate);

      if (check || emailTemplate === 'No Template') {
        setVerifyCheckOldMessage(false);
      } else {
        setVerifyCheckOldMessage(true);
      }
    }
  }, [emailTemplate, CommDetailsProps.emailTemplates]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (CommDetailsProps.RemoveComms?.status === 200) {
      setDefaultValues();
      dispatch(removeCommsFailure({ error: '' }));
    }
  }, [CommDetailsProps?.RemoveComms]); //eslint-disable-line react-hooks/exhaustive-deps

  const validForm = () => {
    if (!messageName || !messageName.trim().length) {
      setError(true);
      return false;
    }
    setError(false);
    return true;
  };

  const handleSave = () => {
    if (validForm()) {
      // Keep existing domain_id on update; create defaults to kindlyhuman (canonical post-collapse).
      const domainId = resolveCommsSaveDomainId({
        existingDomainId: CommDetailsProps?.data?.domain_id,
        domains,
      });
      if (!domainId) {
        toast.error('Missing domain configuration');
        return;
      }

      const payload: SystemMessageType = {
        message: textMessage?.trim(),
        name: messageName?.trim(),
        send_sms: sendAsSMS,
        email_template: emailTemplate === 'No Template' ? null : emailTemplate,
        group_id: messageGroup ? Number(messageGroup) : null,
        audience: messageAudience || 'shared',
        send_email: enableEmail,
        delay_while_on_call: delayWhileOnCall,
        send_sms_as_primary: sendSMSAsPrimary,
        key: messagekey?.trim(),
        subject: subject?.trim(),
        description: internalNotes?.trim(),
        sms_channel_id: smsChannelId ? Number(smsChannelId) : null,
        domain_id: domainId,
      };

      if (!payload['send_sms']) {
        payload['sms_channel_id'] = null;
      }

      if (CommDetailsProps?.data?.id) {
        const updateCommsData = {
          commsId: CommDetailsProps.data.id,
          payload,
        };
        dispatch(updateCommsRequest(updateCommsData));
      } else {
        dispatch(postCommsRequest(payload));
      }
      setEnableSaveOption(false);
    } else {
      CommDetailsProps.handleContinueSave('');
    }
  };

  const handleDelete = () => {
    dispatch(
      removeCommsRequest({
        commsId: CommDetailsProps.data.id,
        name: CommDetailsProps.data.name,
      }),
    );
    setIsDelete(false);
  };

  const handleRepairtemplate = () => {
    dispatch(postRepairTemplateRequest({ template: emailTemplate }));
  };

  const handleCommstest = () => {
    const re = /\S+@\S+\.\S+/;
    if (testEmailAddress?.length && re.test(testEmailAddress)) {
      localStorage.setItem('commsTestEmail', testEmailAddress ? testEmailAddress : '');
      setEnableTestPopUP(false);

      dispatch(
        postTestCommsRequest({
          commsId: CommDetailsProps.data.id,
          payload: { email_address: testEmailAddress },
        }),
      );
    } else {
      if (!testEmailAddress) {
        toast.error('Please enter mail address');
      } else if (!re.test(testEmailAddress)) {
        toast.error('Please enter a valid mail address');
      }
    }
  };

  return (
    <>
      <RouteLeavingGuard
        when={enableSaveOption}
        navigate={(path: string) => history.push(path)}
        shouldBlockNavigation={(location: Location) => {
          return `${location.pathname}${location.search}` !== ROUTE_PATH.TEMPLATES_COMMS;
        }}
        titleText={'Alert'}
        contentText={'You have unsaved changes. Do you want to leave this page and lose your changes?'}
        cancelButtonText={'Cancel'}
        confirmButtonText={'Disregard changes'}
        confirmSaveButtonText="Continue and Save changes"
        handleContinueSaveChange={(path) => {
          handleSave();
          CommDetailsProps.handleContinueSave(path);
          setEnableSaveOption(false);
        }}
      />
      <div className="w-full">
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-end gap-2 border-b border-gray-100 bg-white px-4 py-3 sm:gap-3 sm:px-6">
          {CommDetailsProps?.data?.id && (
            <>
              <button
                className="rounded-full bg-gray-300 px-5 py-1.5 text-center text-sm text-white"
                onClick={() => setIsDelete(true)}
                data-testid="remove-button"
              >
                REMOVE
              </button>

              <button
                className="bg-blue-primary rounded-full px-5 py-1.5 text-center text-sm text-white"
                onClick={() => {
                  setEnableTestPopUP(true);
                }}
                data-testid="test-button"
              >
                TEST
              </button>
            </>
          )}
          <button
            className={
              'rounded-full px-5 py-1.5 text-center text-sm text-white ' +
              (enableSaveOption ? ' bg-blue-primary' : ' cursor-not-allowed bg-gray-300')
            }
            disabled={!enableSaveOption}
            onClick={handleSave}
            data-testid="comms-save-button"
          >
            SAVE
          </button>
        </div>
        {veriyCheckOldMessage && CommDetailsProps?.data && (
          <div className="text-red w-full py-5 text-center text-lg">
            {' '}
            This is an Old Message Please change the Email Template to change{' '}
          </div>
        )}
        <div className="space-y-5 px-4 py-5 sm:px-6">
          <CustomInput
            inputType={'text'}
            value={messageName}
            setValue={(data) => {
              setEnableSaveOption(true);
              setMessageName(data);
            }}
            Label={'Message Name'}
            ErrorMessage={'Field is Required'}
            Error={error && (!messageName || !messageName.trim().length)}
            dataTestId="comms-name"
          />
          <CustomInput
            inputType={'text'}
            value={internalNotes}
            setValue={(data) => {
              setEnableSaveOption(true);
              setInternalNotes(data);
            }}
            Label={'Internal Description'}
            dataTestId="comms-description"
          />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <DropDown
              value={messageGroup}
              setValue={(data) => {
                setEnableSaveOption(true);
                setMessageGroup(data);
              }}
              Label={'Message Group'}
              data-testid="comms-message-group"
            >
              <option key="group_none" value={undefined}>
                No Group
              </option>
              {CommDetailsProps.SystemMessageGroups?.length > 0 &&
                CommDetailsProps.SystemMessageGroups.map((item: any) => (
                  <option key={`group_${item.id}`} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </DropDown>
            <CustomInput
              inputType={'text'}
              value={messagekey}
              setValue={(data) => {
                setEnableSaveOption(true);
                setMessageKey(data);
              }}
              Label={'Message Key'}
              dataTestId="comms-message-key"
            />
            <DropDown
              value={messageAudience}
              setValue={(data) => {
                setEnableSaveOption(true);
                setMessageAudience(data as SystemMessageAudience);
              }}
              Label={'Audience'}
              data-testid="comms-message-audience"
            >
              {AUDIENCE_OPTIONS.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </DropDown>
          </div>
          <CustomInput
            inputType={'text'}
            value={subject}
            setValue={(data) => {
              setEnableSaveOption(true);
              setSubject(data);
            }}
            Label={'Subject'}
            dataTestId="comms-subject"
          />
          <div>
            <span className="text-blue-dark mb-2 block text-sm font-medium leading-4">Message</span>
            <textarea
              value={textMessage}
              onChange={(e: any) => {
                setTextMessage(e.target.value);
                setEnableSaveOption(true);
              }}
              className="text-gray-dark bg-gray-background-light h-28 w-full rounded-md px-3 py-2 text-sm font-extrabold leading-4 focus:outline-none"
              data-testid="comms-message"
            />
          </div>
        </div>

        <div className="mx-4 mb-6 space-y-3 border-t border-gray-100 pt-5 sm:mx-6">
          {/* Email */}
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <label className="text-blue-dark flex items-center gap-2 text-sm font-bold">
              <ToggleButton
                id={'enable-email'}
                status={enableEmail}
                setStatus={(data) => {
                  setEnableSaveOption(true);
                  setEnableEmail(data);
                }}
                initialstatus={true}
                data-testid="enable-email-toggle"
              />
              Email
            </label>
            {enableEmail && (
              <div className="mt-3 space-y-3 border-t border-gray-200/80 pt-3">
                <DropDown
                  Label={'Email Template'}
                  value={emailTemplate}
                  setValue={(data) => {
                    setEnableSaveOption(true);
                    setEmailTemplate(data);
                  }}
                  data-testid="comms-email-template"
                >
                  <option value="No Template">No Template</option>
                  {templatesForPicker(CommDetailsProps.emailTemplates, emailTemplate).map((item) => (
                    <option key={item.id} value={item.id}>
                      {formatEmailTemplateOptionLabel(item)}
                    </option>
                  ))}
                </DropDown>
                {emailTemplate && emailTemplate !== 'No Template' && (
                  <div className="flex flex-wrap gap-2">
                    <button
                      className="bg-blue-primary rounded-full px-4 py-1.5 text-center text-sm text-white"
                      onClick={() => setShowEmailPreview(true)}
                      data-testid="comms-preview-template"
                      type="button"
                    >
                      PREVIEW
                    </button>
                    {CommDetailsProps.data && (
                      <button
                        className="bg-blue-primary rounded-full px-4 py-1.5 text-center text-sm text-white"
                        onClick={handleRepairtemplate}
                        data-testid="comms-repair-template"
                        type="button"
                      >
                        REPAIR
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SMS */}
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <label className="text-blue-dark flex items-center gap-2 text-sm font-bold">
              <ToggleButton
                id={'send-as-sms'}
                status={sendAsSMS}
                setStatus={(data) => {
                  setEnableSaveOption(true);
                  setSendAsSMS(data);
                  if (!data) {
                    setSMSChannelId('');
                  }
                }}
                initialstatus={true}
                data-testid="send-as-sms-toggle"
              />
              SMS text
            </label>
            {sendAsSMS && (
              <div className="mt-3 space-y-3 border-t border-gray-200/80 pt-3">
                <DropDown
                  value={smsChannelId}
                  setValue={(data) => {
                    setSMSChannelId(data ?? '');
                    setEnableSaveOption(true);
                  }}
                  disabled={!sendAsSMS}
                  Label={'SMS Channel'}
                  data-testid="comms-sms-channel"
                >
                  {getAvailableSMSChannelOptions()}
                </DropDown>
                <label className="text-blue-dark flex items-center gap-2 text-sm font-medium">
                  <ToggleButton
                    id={'send-sms-as-primary'}
                    status={sendSMSAsPrimary}
                    setStatus={(data) => {
                      setEnableSaveOption(true);
                      setSendSMSAsPrimary(data);
                    }}
                    initialstatus={true}
                    disabled={!sendAsSMS}
                    data-testid="send-sms-as-primary-toggle"
                  />
                  SMS is primary channel
                </label>
              </div>
            )}
          </div>

          {/* Delay */}
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <label className="text-blue-dark flex items-center gap-2 text-sm font-medium">
              <ToggleButton
                id={'delay-while-on-call'}
                status={delayWhileOnCall}
                setStatus={(data) => {
                  setEnableSaveOption(true);
                  setDelayWhileOnCall(data);
                }}
                initialstatus={true}
                data-testid="delay-while-on-call-toggle"
              />
              Delay sending while user is on call
            </label>
          </div>
        </div>
        {
          <Modal
            isModel={enableTestPopUP}
            InnerComponent={
              <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 w-full text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <h3 className="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                        TEST COMMS EMAIL
                      </h3>
                      <div className="my-5 w-full">
                        <CustomInput
                          inputType={'text'}
                          value={testEmailAddress}
                          setValue={setTestEmailAddress}
                          Label={'Enter Email Address'}
                          dataTestId="comms-test-email-address"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    onClick={handleCommstest}
                    className="stacked-bar-blue-400 inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                    data-testid="comms-test-button"
                  >
                    TEST
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEnableTestPopUP(false);
                    }}
                    className="stacked-bar-blue-400 inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                    data-testid="comms-close-button"
                  >
                    CLOSE
                  </button>
                </div>
              </div>
            }
          />
        }
        {showEmailPreview && emailTemplate && emailTemplate !== 'No Template' && (
          <EmailPreviewModal
            templateName={emailTemplate}
            subject={subject}
            messageBody={textMessage}
            onClose={() => setShowEmailPreview(false)}
          />
        )}
      </div>
      <Modal
        isModel={isDelete}
        InnerComponent={
          <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                    Alert
                  </h3>
                  <div className="mt-2 flex">
                    <p className="flex items-center text-sm text-gray-500">
                      Are you sure you want to remove this {messageName}?
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                onClick={handleDelete}
                className="stacked-bar-blue-400 inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                data-testid="comms-remove-button"
              >
                Remove
              </button>
              <button
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:ml-3 sm:mt-0 sm:w-auto sm:text-sm"
                onClick={() => {
                  setIsDelete(false);
                }}
                data-testid="comms-cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        }
      ></Modal>
    </>
  );
};

const CommsTemplate: React.FunctionComponent<props> = (props): JSX.Element => {
  const { data: emailTemplates } = useEmailTemplateMetadata();
  const dispatch = useDispatch();
  const [selectedCommsData, setselectedCommsData] = useState<any>();
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilterKey>('all');
  const [groupFilter, setGroupFilter] = useState<GroupFilterKey>('all');
  const [typeFilter, setTypeFilter] = useState<ChannelTypeFilterKey>('all');
  const [isTemplateModel, setIsTemplateModel] = useState<boolean>(false);
  const [searchText, setsearchText] = useState('');
  const debouncedSearchText = DeBounce(searchText, 500);
  const [enableRepairTemplate, setenableRepairTemplate] = useState<boolean>(false);
  const [saveLocationPath, setSaveLocationPath] = useState<string>('');
  const history = useHistory();
  const formPaneRef = useRef<HTMLDivElement>(null);
  const SystemMessageGroups = props.SystemMessageGroups || [];

  const handleTemplatesModel = () => {
    setIsTemplateModel(!isTemplateModel);
  };
  useEffect(() => {
    dispatch(fetchSystemMessageGroupsRequest({}));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLatestData = useCallback(() => {
    dispatch(
      fetchCommsListRequest(
        buildCommsListParams({
          groupFilter,
          audienceFilter,
          searchText: debouncedSearchText,
        }),
      ),
    );
  }, [groupFilter, audienceFilter, debouncedSearchText, dispatch]);

  useEffect(() => {
    fetchLatestData();
  }, [fetchLatestData]);

  useEffect(() => {
    if (props.RemoveComms?.status === 200 || props.UpdateComms?.status === 200 || props.CreateComms?.status === 200) {
      if (props.RemoveComms?.status === 200) {
        dispatch(removeCommsFailure({ error: '' }));
      }
      if (props.CreateComms?.status === 200) {
        setselectedCommsData(props.CreateComms.data);
        dispatch(postCommsFailure({ error: '' }));
      }
      if (props.UpdateComms?.status === 200) {
        const data = props.UpdateComms.data;
        setselectedCommsData(data);
        saveLocationPath !== '' && history.push(saveLocationPath);
        dispatch(updateCommsFailure({ error: '' }));
      }
      fetchLatestData();
    }
  }, [props.UpdateComms, props.RemoveComms, props.CreateComms]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.RepairTemplate?.data) {
      setenableRepairTemplate(true);
    }
  }, [props.RepairTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enableRepairTemplate) {
      dispatch(postRepairTemplateSuccess({ LISTENERS: null }));
    }
  }, [enableRepairTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.comms && props.comms?.length > 0) {
      const selectedId = selectedCommsData?.id || null;
      if (selectedId) {
        const selected = props.comms.find((item: any) => item.id === selectedId);
        setselectedCommsData(selected ? selected : null);
      }
    } else {
      setselectedCommsData(null);
    }
    saveLocationPath !== '' && history.push(saveLocationPath);
  }, [props.comms]); //eslint-disable-line react-hooks/exhaustive-deps

  const filteredComms = applyChannelTypeFilter(props.comms, typeFilter);

  const filterSelectClass =
    'w-full rounded-md bg-white px-2 py-1.5 text-xs text-gray-dark ring-1 ring-gray-200 focus:outline-none focus:ring-transparent';

  return (
    <div>
      {props.pending && <Spinner />}
      <Header heading={COMMS_IA.pageHeading} />

      <div className="gray-border-line bg-gray-background-light flex w-full flex-wrap items-center justify-between gap-2 border-b px-3 py-1.5">
        <div className="flex flex-wrap items-center">
          <button
            className="bg-blue-primary whitespace-nowrap px-3 py-2 text-xs font-bold text-white sm:px-4 sm:text-sm"
            onClick={() => {
              history.push(ROUTE_PATH.TEMPLATES_COMMS);
            }}
            data-testid="system-comms-button"
          >
            {COMMS_IA.transactionalMessages}
          </button>
        </div>
        <button
          className="bg-gray-light shrink-0 rounded-full px-3 py-1 text-center text-xs text-white sm:px-4 sm:text-sm"
          type="button"
          onClick={handleTemplatesModel}
        >
          VARIABLE KEY
        </button>
      </div>

      <p className="text-gray-dark px-7 py-3 text-sm">
        Transactional messages are automated system sends (password reset, reminders, invites). Audience is stored on
        each message (not Mandrill labels).
      </p>

      <div className="flex flex-col gap-3 overflow-hidden px-4 pb-4 xl:h-[calc(100vh-11rem)] xl:flex-row xl:gap-6 xl:px-7">
        <aside className="flex max-h-[36vh] min-h-0 w-full shrink-0 flex-col overflow-hidden rounded-lg bg-gray-50 xl:h-full xl:max-h-full xl:w-72">
          <div className="shrink-0 space-y-2.5 border-b border-gray-200 px-3 py-3" data-testid="comms-list-filters">
            <div>
              <label
                className="text-blue-dark mb-1 block text-xs font-semibold uppercase tracking-wide"
                htmlFor="comms-filter-audience"
              >
                Audience
              </label>
              <select
                id="comms-filter-audience"
                className={filterSelectClass}
                value={audienceFilter}
                onChange={(e) => setAudienceFilter(e.target.value as AudienceFilterKey)}
                data-testid="comms-audience-filter"
              >
                {AUDIENCE_FILTERS.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="text-blue-dark mb-1 block text-xs font-semibold uppercase tracking-wide"
                htmlFor="comms-filter-group"
              >
                Group
              </label>
              <select
                id="comms-filter-group"
                className={filterSelectClass}
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value as GroupFilterKey)}
                data-testid="comms-group-filter"
              >
                <option value="all">All</option>
                <option value="null">No Group</option>
                {SystemMessageGroups.map((item: any) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="text-blue-dark mb-1 block text-xs font-semibold uppercase tracking-wide"
                htmlFor="comms-filter-type"
              >
                Type
              </label>
              <select
                id="comms-filter-type"
                className={filterSelectClass}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as ChannelTypeFilterKey)}
                data-testid="comms-type-filter"
              >
                {CHANNEL_TYPE_FILTERS.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="text-blue-dark mb-1 block text-xs font-semibold uppercase tracking-wide"
                htmlFor="comms-filter-search"
              >
                Search
              </label>
              <input
                id="comms-filter-search"
                type="text"
                className={filterSelectClass}
                value={searchText}
                onChange={(e) => setsearchText(e.target.value)}
                placeholder="Message name"
                data-testid="comms-search-input"
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 py-2">
            {filteredComms.map((item: any) => (
              <button
                key={item.id ?? item.name}
                type="button"
                className={
                  'w-full rounded-md px-3 py-2.5 text-left text-sm ' +
                  (selectedCommsData?.id === item.id
                    ? 'bg-blue-primary font-bold text-white'
                    : 'text-gray-dark bg-white hover:bg-gray-100')
                }
                onClick={() => {
                  setselectedCommsData({ ...item });
                  setSaveLocationPath('');
                  formPaneRef.current?.scrollTo({ top: 0 });
                }}
              >
                {item.name}
              </button>
            ))}
            {filteredComms.length === 0 && (
              <p className="text-gray-dark px-2 py-4 text-sm">
                {props.listPending ? 'Loading…' : 'No messages for these filters. Try All, or clear search.'}
              </p>
            )}
          </div>
        </aside>

        <div ref={formPaneRef} className="min-h-0 min-w-0 flex-1 overflow-y-auto rounded-lg bg-white">
          <CommsDetails
            data={selectedCommsData}
            key={selectedCommsData?.id ?? 'new'}
            emailTemplates={emailTemplates || []}
            SystemMessageGroups={SystemMessageGroups}
            RemoveComms={props.RemoveComms}
            handleContinueSave={setSaveLocationPath}
          />
        </div>
      </div>
      {isTemplateModel && <TemplateModel handleToggleModel={handleTemplatesModel} />}
      {
        <Modal
          isModel={enableRepairTemplate}
          InnerComponent={
            <div className="z-100 fixed inset-0 flex items-center justify-center overflow-x-hidden outline-none focus:outline-none">
              <div className="min-w-3xl max-h-162.5 modal-border-shadow relative mx-auto my-6 w-auto max-w-5xl overflow-y-auto rounded-lg">
                <div className="relative flex w-full flex-col rounded-lg border-0 bg-white shadow-lg outline-none focus:outline-none">
                  <div className="flex items-start justify-between">
                    <div className="blue-primary px-10 pt-10 text-2xl">Email Template Report</div>
                  </div>
                  <div className="relative flex-auto px-10 pt-2">
                    <p className="text-gray-dark h-full text-base font-normal leading-relaxed">Report Summary:</p>
                    <p className="flex items-center">
                      <p className="bg-gray-dark mr-2 h-3 w-3 rounded-full"></p>
                      Total Records:{' '}
                      {props.RepairTemplate?.data?.processed.length + props.RepairTemplate?.data?.errors.length}
                    </p>
                    <p className="flex items-center">
                      <p className="bg-gray-dark mr-2 h-3 w-3 rounded-full"></p>
                      Processed Templates : {props.RepairTemplate?.data?.processed.length}
                    </p>
                    <p className="flex items-center">
                      <p className="bg-gray-dark mr-2 h-3 w-3 rounded-full"></p>
                      Errored Templates: {props.RepairTemplate?.data?.errors.length}
                    </p>
                  </div>

                  <div className="flex items-start justify-between">
                    <div className="blue-primary px-10 pt-10 text-2xl">Process Details:</div>
                  </div>
                  <table className="table-row-format relative mx-10 my-2 table-fixed border-collapse">
                    <thead className="h-full bg-white">
                      <tr className="w-full px-7 text-left">
                        <th className="text-gray-dark sticky top-0 mb-4 h-full w-6/12 bg-white px-2 py-2 text-base font-normal">
                          Template name
                        </th>
                        <th className="text-gray-dark sticky top-0 mb-4 h-full w-6/12 bg-white px-2 py-2 text-base font-normal">
                          Message
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {props.RepairTemplate?.data?.processed.length > 0 ? (
                        props.RepairTemplate?.data?.processed.map((item: any) => (
                          <tr
                            role="button"
                            className="text-gray-dark h-12 space-y-6 rounded border-4 border-y border-white text-left"
                          >
                            <td className="border-bottom w-6/12 py-1">
                              <div className="flex">
                                <p className="text-gray-dark text-bold pr-2 text-base font-bold">{item.name}</p>
                              </div>
                            </td>
                            <td className="border-bottom w-6/12 py-1">
                              <div className="flex">
                                <p className="text-gray-dark text-bold pr-2 text-base font-bold">{item.message}</p>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr
                          role="button"
                          className="text-gray-dark h-12 space-y-6 rounded border-4 border-y border-white text-left"
                        >
                          <td className="border-bottom w-1/2 py-1">
                            <div className="flex">
                              <p className="text-gray-dark text-bold pr-2 text-base font-bold">No Data Available</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* errors table */}
                  <div className="flex items-start justify-between">
                    <div className="blue-primary px-10 pt-10 text-2xl">Error Details:</div>
                  </div>
                  <table className="table-row-format relative mx-10 my-2 table-fixed border-collapse">
                    <thead className="h-full bg-white">
                      <tr className="w-full px-7 text-left">
                        <th className="text-gray-dark sticky top-0 mb-4 h-full w-6/12 bg-white px-2 py-2 text-base font-normal">
                          Template name
                        </th>
                        <th className="text-gray-dark sticky top-0 mb-4 h-full w-6/12 bg-white px-2 py-2 text-base font-normal">
                          Message
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {props.RepairTemplate?.data?.errors.length > 0 ? (
                        props.RepairTemplate?.data?.errors.map((item: any) => (
                          <tr
                            role="button"
                            className="text-gray-dark h-12 space-y-6 rounded border-4 border-y border-white text-left"
                          >
                            <td className="border-bottom w-2/12 py-1">
                              <div className="flex">
                                <p className="text-gray-dark text-bold pr-2 text-base font-bold">{item.name}</p>
                              </div>
                            </td>
                            <td className="border-bottom w-6/12 py-1">
                              <div className="flex">
                                <p className="text-gray-dark text-bold pr-2 text-base font-bold">{item.message}</p>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr
                          role="button"
                          className="text-gray-dark h-12 space-y-6 rounded border-4 border-y border-white text-left"
                        >
                          <td className="border-bottom w-1/2 py-1">
                            <div className="flex">
                              <p className="text-gray-dark text-bold pr-2 text-base font-bold">No Data Available</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div className="flex items-center justify-center pb-6">
                    <button
                      className="bg-gray-light text-cente rounded-full px-7 py-2 text-sm text-white"
                      type="button"
                      onClick={() => {
                        setenableRepairTemplate(false);
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          }
        />
      }
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    comms: state.Comms.CommsList?.data || null,
    SystemMessageGroups: state.Comms.SystemMessageGroups?.data || null,
    pending: state.Comms.pending || null,
    listPending: state.Comms.listPending || false,
    UpdateComms: state.Comms.UpdateComms || null,
    RemoveComms: state.Comms.RemoveComms || null,
    CreateComms: state.Comms.Comms || null,
    RepairTemplate: state.Comms.RepairTemplate || null,
  };
};

export default connect(mapStateToProps)(CommsTemplate);
